import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../AuthComponent';
import { AlertTriangleIcon, CheckCircleIcon, UploadIcon } from '@shopify/polaris-icons';
import { Ic, StatusPill } from '../ui';

// Settings > Purchase Orders: the Shopify admin session the Purchase Orders
// tool reads POs with. Shopify's PO admin has no API, so the session is a set
// of cookies taken from a HAR file (or pushed by the Mac session-warmer).
// Two cards: the session as it stands, and uploading a fresh HAR file.

const API = window.location.origin;

async function authed(url, options = {}) {
  const token = localStorage.getItem('access_token');
  const headers = { Authorization: `Bearer ${token}`, ...(options.headers || {}) };
  const res = await fetch(url, { ...options, headers });
  if (res.status === 401) throw new Error('AUTH_FAILED');
  return res;
}

const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString('en-US', {
  month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York',
}) + ' ET' : '—');

function ago(iso) {
  if (!iso) return '';
  const h = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 3600000));
  if (h < 1) return 'less than an hour ago';
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
  const d = Math.round(h / 24);
  return `${d} day${d === 1 ? '' : 's'} ago`;
}

export default function PurchaseOrdersSettings() {
  const { nameOf } = useAuth();
  const [status, setStatus] = useState(null);       // null = checking; {ok, ...} after
  const [checkedAt, setCheckedAt] = useState(null);
  const [checking, setChecking] = useState(false);
  const [uploads, setUploads] = useState([]);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [notice, setNotice] = useState(null);       // {text, tone}
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);

  const check = useCallback(async () => {
    setChecking(true);
    try {
      const res = await authed(`${API}/api/purchase-orders/status`);
      const data = res.ok ? await res.json() : null;
      setStatus(data && data.status === 'ok' ? { ok: true, ...data } : { ok: false });
    } catch { setStatus({ ok: false }); }
    finally { setChecking(false); setCheckedAt(new Date()); }
  }, []);

  const loadUploads = useCallback(async () => {
    try {
      const res = await authed(`${API}/api/purchase-orders/session-history`);
      if (res.ok) setUploads((await res.json()).uploads || []);
    } catch { /* history is a nicety */ }
  }, []);

  useEffect(() => { check(); loadUploads(); }, [check, loadUploads]);

  const pick = (f) => {
    if (!f) return;
    if (!/\.har$/i.test(f.name)) { setNotice({ text: 'That is not a .har file.', tone: 'crit' }); return; }
    setFile(f); setNotice(null);
  };

  const upload = async () => {
    if (!file) return;
    setUploading(true); setNotice(null);
    try {
      const form = new FormData();
      form.append('har', file);
      const res = await authed(`${API}/api/purchase-orders/upload-har`, { method: 'POST', body: form });
      if (!res.ok) {
        let detail = 'Upload failed.';
        try { detail = (await res.json()).detail || detail; } catch { /* keep default */ }
        throw new Error(detail);
      }
      const data = await res.json();
      setNotice({ text: `Session updated: ${data.cookies_count} cookies taken from ${file.name}.`, tone: 'ok' });
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
      await Promise.all([check(), loadUploads()]);
    } catch (e) {
      setNotice({ text: e.message || 'Upload failed.', tone: 'crit' });
    } finally { setUploading(false); }
  };

  const pill = status === null
    ? <StatusPill tone="neu">Checking…</StatusPill>
    : <StatusPill tone={status.ok ? 'ful' : 'can'}>{status.ok ? 'Active' : 'Expired'}</StatusPill>;

  return (
    <div className="uh-po-settings">
      {notice && (
        <div className={`uh-alert ${notice.tone}`} role="status">
          <Ic source={notice.tone === 'ok' ? CheckCircleIcon : AlertTriangleIcon} sm /><div>{notice.text}</div>
        </div>
      )}
      <div className="uh-po-st-grid">
        <div className="uh-card">
          <div className="uh-card-h"><h2 className="uh-h2 lg">Session</h2><span className="uh-sp" />{pill}</div>
          <div className="uh-card-b uh-po-st-body">
            {status && !status.ok && (
              <p className="uh-help">No usable session is saved. Purchase orders can't be loaded or printed until a fresh HAR file is uploaded.</p>
            )}
            <dl className="uh-po-kv">
              <dt>Updated</dt><dd>{status?.ok ? `${fmtWhen(status.updated_at)} · ${ago(status.updated_at)}` : '—'}</dd>
              <dt>Expires</dt><dd>{status?.ok && status.expires_at ? fmtWhen(status.expires_at) : status?.ok ? 'Not reported by Shopify' : '—'}</dd>
              <dt>Cookies</dt><dd>{status?.ok ? `${status.cookies_count} saved from the last upload` : '—'}</dd>
              <dt>Last checked</dt><dd>{checkedAt ? `${checkedAt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })} · ${status?.ok ? 'session file is in place' : 'no session'}` : '—'}</dd>
            </dl>
            <div><button type="button" className="uh-btn" disabled={checking} onClick={check}>{checking ? 'Checking…' : 'Check session now'}</button></div>
            <div className="uh-po-hist">
              <b>Recent uploads</b>
              {uploads.length === 0
                ? <span>No manual uploads recorded yet. The Mac session-warmer may also refresh the session on its own.</span>
                : uploads.map((u) => <span key={u.at + u.username}>{fmtWhen(u.at)} · {nameOf(u.username)} · {u.text.replace(/^Uploaded a new Shopify session\s*/, '').replace(/^\((.*)\)$/, '$1') || 'HAR'}</span>)}
            </div>
          </div>
        </div>

        <div className="uh-card">
          <div className="uh-card-h"><h2 className="uh-h2 lg">Update session</h2></div>
          <div className="uh-card-b uh-po-st-body">
            <p className="uh-help">Upload a HAR file captured from Shopify Admin. Sessions last one to six months; do this only when the session has expired.</p>
            <div
              className={`uh-po-drop${dragging ? ' over' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => { e.preventDefault(); setDragging(false); pick(e.dataTransfer.files[0]); }}
              onClick={() => fileRef.current?.click()}
              role="button" tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileRef.current?.click(); } }}
            >
              <Ic source={UploadIcon} />
              <b>{file ? file.name : 'Drop a .har file here, or choose one'}</b>
              <span>{file ? `${Math.round(file.size / 1024).toLocaleString()} KB · ready to upload` : 'Only the session cookies are kept; the file itself is discarded.'}</span>
              <input ref={fileRef} id="po-har-file" type="file" accept=".har" hidden onChange={(e) => pick(e.target.files[0])} />
            </div>
            <div className="uh-po-st-acts">
              <button type="button" className="uh-btn" onClick={() => fileRef.current?.click()}>Choose file</button>
              <span className="uh-help">{file ? file.name : 'No file chosen'}</span>
              <span className="uh-sp" />
              <button type="button" className={`uh-btn primary${uploading ? ' busy' : ''}`} disabled={!file || uploading} onClick={upload}>{uploading ? 'Uploading…' : 'Upload'}</button>
            </div>
            <div>
              <h3 className="uh-h2" style={{ marginBottom: 6 }}>How to capture a HAR file</h3>
              <ol className="uh-po-steps">
                <li>Sign in to Shopify Admin in Chrome.</li>
                <li>Open DevTools (<code>F12</code>) and pick the <b>Network</b> tab.</li>
                <li>Go to Purchase orders and open any PO; wait for it to load fully.</li>
                <li>Right-click in the Network list and choose <b>Save all as HAR with content</b>.</li>
                <li>Upload that file here.</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
